import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import {
  PG_LISTINGS, MY_PG_LISTINGS,
  CREATE_PG_LISTING, UPDATE_PG_LISTING, DELETE_PG_LISTING,
  SHOW_PG_INTEREST, WITHDRAW_PG_INTEREST,
} from '../graphql';
import {
  Home, MapPin, Plus, X, Search, Users, BedDouble, Phone,
  Trash2, Pencil, Heart, CalendarDays, GraduationCap, MessageCircle, ImagePlus,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const LISTING_TYPES = [
  { value: 'LOOKING',  label: 'Looking for PG / flatmate', short: 'Looking',  color: 'bg-amber-100 text-amber-700' },
  { value: 'OFFERING', label: 'Room / PG available',       short: 'Available', color: 'bg-green-100 text-green-700' },
];
const ROOM_TYPES = [
  { value: 'SINGLE', label: 'Single room' },
  { value: 'DOUBLE', label: 'Double sharing' },
  { value: 'TRIPLE', label: 'Triple sharing' },
  { value: 'SHARED', label: 'Shared / any' },
];
const GENDERS = [
  { value: 'ANY',    label: 'Any gender',  color: 'bg-gray-100 text-gray-600' },
  { value: 'MALE',   label: 'Male only',   color: 'bg-blue-100 text-blue-700' },
  { value: 'FEMALE', label: 'Female only', color: 'bg-pink-100 text-pink-700' },
];
const AMENITY_PRESETS = ['WiFi', 'AC', 'Food / Mess', 'Laundry', 'Attached Bathroom', 'Power Backup', 'Housekeeping', 'Parking', 'Fridge', 'Gym'];

const inr = (n) => `₹${new Intl.NumberFormat('en-IN').format(n)}`;
const labelFor = (list, value) => list.find(o => o.value === value)?.label || value;

const EMPTY_FORM = {
  listingType: 'LOOKING', title: '', description: '', location: '', nearCollege: '', city: '',
  budgetMin: '', budgetMax: '', preferredGender: 'ANY', roomType: 'SHARED', occupancy: '',
  moveInDate: '', amenities: [], preferencesInput: '', contact: '', contactWhatsapp: false,
};

const MAX_PHOTOS = 3;

// Reads a file, downscales it on a canvas (max 900px long edge) and returns a JPEG data URL.
async function fileToDataUrl(file) {
  if (file.size > 8 * 1024 * 1024) {
    throw new Error(`"${file.name}" is larger than 8MB — please pick a smaller photo.`);
  }
  const rawUrl = await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error(`Couldn't read "${file.name}". Please try again.`));
    reader.readAsDataURL(file);
  });
  const img = await new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error(`"${file.name}" doesn't look like a valid image.`));
    image.src = rawUrl;
  });
  const scale = Math.min(1, 900 / Math.max(img.width, img.height));
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(img.width * scale));
  canvas.height = Math.max(1, Math.round(img.height * scale));
  canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
  return canvas.toDataURL('image/jpeg', 0.8);
}

function Chip({ className = 'bg-gray-100 text-gray-600', children }) {
  return <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium ${className}`}>{children}</span>;
}

function CoverPhoto({ images, title }) {
  const [idx, setIdx] = useState(0);
  if (!images || images.length === 0) return null;
  const i = idx % images.length;
  return (
    <button type="button" onClick={() => setIdx(v => (v + 1) % images.length)}
      title={images.length > 1 ? 'Click for next photo' : undefined}
      className="relative block w-full mb-3 focus:outline-none">
      <img src={images[i]} alt={title} className="h-36 w-full object-cover rounded-t-xl" />
      <span className="absolute bottom-2 right-2 text-[11px] font-medium text-white bg-black/60 px-2 py-0.5 rounded-full">
        {i + 1}/{images.length} 📷
      </span>
    </button>
  );
}

function ListingCard({ listing, currentUserId, onInterest, onWithdraw, onEdit, onDelete, togglingId }) {
  const isOwn = listing.poster?.id === currentUserId;
  const typeCfg = LISTING_TYPES.find(t => t.value === listing.listingType) || LISTING_TYPES[0];
  const genderCfg = GENDERS.find(g => g.value === listing.preferredGender) || GENDERS[0];
  const rent = listing.budgetMin > 0 && listing.budgetMin !== listing.budgetMax
    ? `${inr(listing.budgetMin)} – ${inr(listing.budgetMax)}`
    : inr(listing.budgetMax);
  const showContact = listing.contact && (isOwn || listing.hasShownInterest);

  return (
    <div className="card hover:shadow-md transition-shadow flex flex-col group">
      {/* Cover photo */}
      <CoverPhoto images={listing.images} title={listing.title} />

      {/* Chips row */}
      <div className="flex items-start justify-between mb-2 gap-2">
        <div className="flex flex-wrap gap-1">
          <Chip className={typeCfg.color}><Home size={11} /> {typeCfg.short}</Chip>
          {listing.status !== 'ACTIVE' && <Chip className="bg-gray-200 text-gray-500">Closed</Chip>}
        </div>
        {isOwn && (
          <div className="flex items-center gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
            <button onClick={() => onEdit(listing)} title="Edit" className="text-gray-300 hover:text-indigo-500 transition-colors"><Pencil size={14} /></button>
            <button onClick={() => onDelete(listing)} title="Delete" className="text-gray-300 hover:text-red-400 transition-colors"><Trash2 size={14} /></button>
          </div>
        )}
      </div>

      {/* Title + location */}
      <h3 className="font-bold text-gray-900 mb-0.5">{listing.title}</h3>
      <p className="flex items-center gap-1 text-xs text-gray-400 mb-2">
        <MapPin size={12} className="flex-shrink-0" />
        <span className="truncate">{[listing.location, listing.city].filter(Boolean).join(', ')}</span>
      </p>
      {listing.nearCollege && (
        <p className="flex items-center gap-1 text-xs text-gray-400 mb-2 -mt-1">
          <GraduationCap size={12} className="flex-shrink-0" /> Near {listing.nearCollege}
        </p>
      )}

      {listing.description && <p className="text-sm text-gray-600 mb-3 line-clamp-2 leading-relaxed">{listing.description}</p>}

      {/* Rent */}
      <div className="flex items-baseline gap-1 mb-3">
        <span className="text-xl font-bold text-indigo-600">{rent}</span>
        <span className="text-xs text-gray-400">/ month</span>
      </div>

      {/* Attribute chips */}
      <div className="flex flex-wrap gap-1 mb-3">
        <Chip className="bg-violet-100 text-violet-700"><BedDouble size={11} /> {labelFor(ROOM_TYPES, listing.roomType)}</Chip>
        <Chip className={genderCfg.color}>{genderCfg.label}</Chip>
        {listing.occupancy > 0 && <Chip><Users size={11} /> {listing.occupancy} {listing.occupancy === 1 ? 'person' : 'people'}</Chip>}
        {listing.moveInDate && <Chip><CalendarDays size={11} /> {listing.moveInDate}</Chip>}
      </div>

      {/* Amenities */}
      {listing.amenities?.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-3">
          {listing.amenities.slice(0, 5).map(a => <span key={a} className="text-xs text-gray-400 bg-gray-50 px-2 py-0.5 rounded-full">{a}</span>)}
          {listing.amenities.length > 5 && <span className="text-xs text-gray-300 px-1 py-0.5">+{listing.amenities.length - 5} more</span>}
        </div>
      )}

      {/* Footer */}
      <div className="mt-auto pt-3 border-t border-gray-50">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-7 h-7 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-700 font-bold text-xs flex-shrink-0">
              {listing.poster?.name?.[0]?.toUpperCase()}
            </div>
            <div className="min-w-0">
              <p className="text-xs font-semibold text-gray-700 truncate">{isOwn ? 'You' : listing.poster?.name}</p>
              {listing.poster?.college && <p className="text-xs text-gray-400 truncate">{listing.poster.college}</p>}
            </div>
          </div>
          <span className="flex items-center gap-1 text-xs text-gray-400 flex-shrink-0">
            <Heart size={12} className={listing.interestCount > 0 ? 'text-red-400' : ''} fill={listing.interestCount > 0 ? 'currentColor' : 'none'} />
            {listing.interestCount} interested
          </span>
        </div>

        {isOwn ? (
          showContact && (
            <p className="flex items-center gap-1.5 text-xs text-gray-400"><Phone size={12} /> {listing.contact}{listing.contactWhatsapp ? ' (WhatsApp)' : ''}</p>
          )
        ) : (
          <>
            <button
              onClick={() => (listing.hasShownInterest ? onWithdraw(listing.id) : onInterest(listing.id))}
              disabled={togglingId === listing.id}
              className={`w-full flex items-center justify-center gap-2 py-2 text-sm font-medium rounded-xl transition-colors disabled:opacity-50 ${
                listing.hasShownInterest
                  ? 'text-gray-500 bg-gray-100 hover:bg-gray-200'
                  : 'text-indigo-600 bg-indigo-50 hover:bg-indigo-100'
              }`}>
              <Heart size={15} fill={listing.hasShownInterest ? 'currentColor' : 'none'} className={listing.hasShownInterest ? 'text-red-400' : ''} />
              {listing.hasShownInterest ? 'Interested — tap to withdraw' : "I'm interested"}
            </button>
            {showContact && (
              <p className="flex items-center gap-1.5 text-xs text-indigo-600 mt-2 font-medium">
                {listing.contactWhatsapp ? <MessageCircle size={12} /> : <Phone size={12} />}
                {listing.contact}{listing.contactWhatsapp ? ' · WhatsApp' : ''}
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default function PgFinderPage() {
  const { user } = useAuth();
  const [tab, setTab] = useState('browse');
  const [filters, setFilters] = useState({ city: '', budgetMax: '', roomType: '', listingType: '', preferredGender: '' });
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editTarget, setEditTarget] = useState(null); // listing being edited
  const [editForm, setEditForm] = useState({ title: '', description: '', budgetMax: '', status: 'ACTIVE', amenities: [] });
  const [togglingId, setTogglingId] = useState(null);
  const [photos, setPhotos] = useState([]);          // compressed data URLs for the create modal
  const [photoBusy, setPhotoBusy] = useState(false); // true while files are being read/compressed
  const [photoError, setPhotoError] = useState('');

  const browseVars = {
    city: filters.city.trim() || undefined,
    budgetMax: filters.budgetMax ? parseInt(filters.budgetMax, 10) : undefined,
    roomType: filters.roomType || undefined,
    listingType: filters.listingType || undefined,
    preferredGender: filters.preferredGender || undefined,
  };

  const { data, loading, error, refetch } = useQuery(PG_LISTINGS, { variables: browseVars });
  const { data: myData, loading: myLoading, error: myError, refetch: myRefetch } = useQuery(MY_PG_LISTINGS, { skip: tab !== 'mine' });

  const refetchAll = [PG_LISTINGS, MY_PG_LISTINGS];
  const [createListing, { loading: creating }] = useMutation(CREATE_PG_LISTING, { refetchQueries: refetchAll });
  const [updateListing, { loading: updating }] = useMutation(UPDATE_PG_LISTING, { refetchQueries: refetchAll });
  const [deleteListing] = useMutation(DELETE_PG_LISTING, { refetchQueries: refetchAll });
  const [showInterest] = useMutation(SHOW_PG_INTEREST, { refetchQueries: refetchAll });
  const [withdrawInterest] = useMutation(WITHDRAW_PG_INTEREST, { refetchQueries: refetchAll });

  const listings = tab === 'browse' ? (data?.pgListings || []) : (myData?.myPgListings || []);
  const isLoading = tab === 'browse' ? loading : myLoading;
  const activeError = tab === 'browse' ? error : myError;
  const hasFilters = Object.values(filters).some(Boolean);

  const toggleAmenity = (setter) => (a) =>
    setter(f => ({ ...f, amenities: f.amenities.includes(a) ? f.amenities.filter(x => x !== a) : [...f.amenities, a] }));

  const handleInterest = async (id) => {
    setTogglingId(id);
    try { await showInterest({ variables: { listingId: id } }); } finally { setTogglingId(null); }
  };
  const handleWithdraw = async (id) => {
    setTogglingId(id);
    try { await withdrawInterest({ variables: { listingId: id } }); } finally { setTogglingId(null); }
  };
  const handleDelete = async (listing) => {
    if (!window.confirm(`Delete listing "${listing.title}"? This cannot be undone.`)) return;
    await deleteListing({ variables: { listingId: listing.id } });
  };
  const openEdit = (listing) => {
    setEditTarget(listing);
    setEditForm({
      title: listing.title, description: listing.description || '',
      budgetMax: String(listing.budgetMax), status: listing.status, amenities: listing.amenities || [],
    });
  };

  const handlePhotoFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = ''; // allow re-selecting the same file
    if (!files.length) return;
    setPhotoError('');
    const room = MAX_PHOTOS - photos.length;
    if (files.length > room) setPhotoError(`You can add up to ${MAX_PHOTOS} photos.`);
    setPhotoBusy(true);
    try {
      const urls = [];
      for (const f of files.slice(0, Math.max(room, 0))) urls.push(await fileToDataUrl(f));
      setPhotos(p => [...p, ...urls].slice(0, MAX_PHOTOS));
    } catch (err) {
      setPhotoError(err.message);
    } finally {
      setPhotoBusy(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    await createListing({ variables: {
      listingType: form.listingType,
      title: form.title.trim(),
      description: form.description.trim() || null,
      location: form.location.trim(),
      nearCollege: form.nearCollege.trim() || null,
      city: form.city.trim() || null,
      budgetMin: form.budgetMin ? parseInt(form.budgetMin, 10) : null,
      budgetMax: parseInt(form.budgetMax, 10),
      preferredGender: form.preferredGender,
      roomType: form.roomType,
      occupancy: form.occupancy ? parseInt(form.occupancy, 10) : null,
      moveInDate: form.moveInDate || null,
      amenities: form.amenities,
      preferences: form.preferencesInput.split(',').map(s => s.trim()).filter(Boolean),
      contact: form.contact.trim() || null,
      contactWhatsapp: form.contactWhatsapp,
      images: photos,
    }});
    setForm(EMPTY_FORM);
    setPhotos([]);
    setPhotoError('');
    setShowCreate(false);
    setTab('mine');
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    await updateListing({ variables: {
      listingId: editTarget.id,
      title: editForm.title.trim(),
      description: editForm.description.trim() || null,
      budgetMax: parseInt(editForm.budgetMax, 10),
      status: editForm.status,
      amenities: editForm.amenities,
    }});
    setEditTarget(null);
  };

  return (
    <div className="max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6 gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">PG Finder</h1>
          <p className="text-gray-500 text-sm mt-1">Find PGs, flats & flatmates near your campus</p>
        </div>
        <button className="btn-primary flex items-center gap-2 flex-shrink-0" onClick={() => setShowCreate(true)}>
          <Plus size={18} /> Post a listing
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-5 bg-gray-100 rounded-xl p-1 w-fit">
        {[{ key: 'browse', label: 'Browse' }, { key: 'mine', label: 'My listings' }].map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${tab === t.key ? 'bg-white text-indigo-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Filters (Browse only) */}
      {tab === 'browse' && (
        <div className="flex gap-3 mb-6 flex-wrap">
          <div className="relative flex-1 min-w-44">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input className="input !pl-9" placeholder="City (e.g. Pune)"
              value={filters.city} onChange={e => setFilters({ ...filters, city: e.target.value })} />
          </div>
          <input className="input w-36" type="number" min="0" placeholder="Max budget ₹"
            value={filters.budgetMax} onChange={e => setFilters({ ...filters, budgetMax: e.target.value })} />
          <select className="input w-40" value={filters.roomType} onChange={e => setFilters({ ...filters, roomType: e.target.value })}>
            <option value="">Any room type</option>
            {ROOM_TYPES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select>
          <select className="input w-44" value={filters.listingType} onChange={e => setFilters({ ...filters, listingType: e.target.value })}>
            <option value="">Looking & available</option>
            {LISTING_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
          <select className="input w-36" value={filters.preferredGender} onChange={e => setFilters({ ...filters, preferredGender: e.target.value })}>
            <option value="">Any gender</option>
            {GENDERS.filter(g => g.value !== 'ANY').map(g => <option key={g.value} value={g.value}>{g.label}</option>)}
          </select>
          {hasFilters && (
            <button onClick={() => setFilters({ city: '', budgetMax: '', roomType: '', listingType: '', preferredGender: '' })}
              className="text-sm text-gray-400 hover:text-indigo-500 transition-colors">Clear</button>
          )}
        </div>
      )}

      {/* Body */}
      {activeError ? (
        <div className="text-center py-16">
          <p className="text-red-500 text-sm mb-3">Couldn't load listings — {activeError.message}</p>
          <button className="btn-secondary text-sm" onClick={() => (tab === 'browse' ? refetch() : myRefetch())}>Retry</button>
        </div>
      ) : isLoading ? (
        <div className="flex items-center justify-center h-48"><div className="animate-spin w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full" /></div>
      ) : listings.length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          <Home size={48} className="mx-auto mb-3 opacity-30" />
          {tab === 'browse'
            ? <p>No PGs found — try widening filters</p>
            : <><p className="mb-3">You haven't posted any listings yet.</p><button className="btn-primary text-sm" onClick={() => setShowCreate(true)}>+ Post your first listing</button></>}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {listings.map(l => (
            <ListingCard key={l.id} listing={l} currentUserId={user?.id} togglingId={togglingId}
              onInterest={handleInterest} onWithdraw={handleWithdraw} onEdit={openEdit} onDelete={handleDelete} />
          ))}
        </div>
      )}

      {/* Create modal */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-gray-100 sticky top-0 bg-white z-10">
              <h2 className="font-bold text-gray-900">Post a PG listing</h2>
              <button onClick={() => setShowCreate(false)}><X size={18} className="text-gray-400" /></button>
            </div>
            <form onSubmit={handleCreate} className="p-5 space-y-4">
              {/* Listing type */}
              <div className="grid grid-cols-2 gap-2">
                {LISTING_TYPES.map(t => (
                  <button key={t.value} type="button" onClick={() => setForm(f => ({ ...f, listingType: t.value }))}
                    className={`p-3 rounded-xl border-2 text-sm font-medium transition-colors ${form.listingType === t.value ? 'border-indigo-500 bg-indigo-50 text-indigo-700' : 'border-gray-100 text-gray-500 hover:border-gray-200'}`}>
                    {t.label}
                  </button>
                ))}
              </div>

              <input required className="input w-full" placeholder="Title (e.g. 2 BHK flatmate needed near VIT)"
                value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} />

              <textarea className="input w-full !h-20 resize-none text-sm" placeholder="Description — room details, house rules, preferences… (optional)"
                value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />

              {/* Photos */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Photos (up to {MAX_PHOTOS})</label>
                <div className="flex flex-wrap gap-2">
                  {photos.map((src, i) => (
                    <div key={i} className="relative w-20 h-20">
                      <img src={src} alt={`Selected ${i + 1}`} className="w-20 h-20 object-cover rounded-xl border border-gray-100" />
                      <button type="button" title="Remove photo"
                        onClick={() => setPhotos(p => p.filter((_, j) => j !== i))}
                        className="absolute -top-1.5 -right-1.5 w-5 h-5 flex items-center justify-center rounded-full bg-gray-800/80 text-white hover:bg-red-500 transition-colors">
                        <X size={12} />
                      </button>
                    </div>
                  ))}
                  {photos.length < MAX_PHOTOS && (
                    <label className={`w-20 h-20 flex flex-col items-center justify-center gap-1 border-2 border-dashed border-gray-200 rounded-xl text-gray-400 transition-colors ${photoBusy ? 'opacity-50 cursor-wait' : 'cursor-pointer hover:border-indigo-400 hover:text-indigo-500'}`}>
                      <ImagePlus size={20} />
                      <span className="text-[10px] font-medium">{photoBusy ? 'Adding…' : 'Add'}</span>
                      <input type="file" accept="image/*" multiple className="hidden" disabled={photoBusy} onChange={handlePhotoFiles} />
                    </label>
                  )}
                </div>
                {photoError && <p className="text-xs text-red-500 mt-1">{photoError}</p>}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Location / area *</label>
                  <input required className="input" placeholder="Kothrud, near D-Mart" value={form.location} onChange={e => setForm(f => ({ ...f, location: e.target.value }))} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                  <input className="input" placeholder="Pune" value={form.city} onChange={e => setForm(f => ({ ...f, city: e.target.value }))} />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Near college</label>
                  <input className="input" placeholder="VIT Pune" value={form.nearCollege} onChange={e => setForm(f => ({ ...f, nearCollege: e.target.value }))} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Budget min (₹/month)</label>
                  <input className="input" type="number" min="0" placeholder="6000" value={form.budgetMin} onChange={e => setForm(f => ({ ...f, budgetMin: e.target.value }))} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Budget max (₹/month) *</label>
                  <input required className="input" type="number" min="1" placeholder="9000" value={form.budgetMax} onChange={e => setForm(f => ({ ...f, budgetMax: e.target.value }))} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Room type</label>
                  <select className="input" value={form.roomType} onChange={e => setForm(f => ({ ...f, roomType: e.target.value }))}>
                    {ROOM_TYPES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Preferred gender</label>
                  <select className="input" value={form.preferredGender} onChange={e => setForm(f => ({ ...f, preferredGender: e.target.value }))}>
                    {GENDERS.map(g => <option key={g.value} value={g.value}>{g.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Occupancy (people)</label>
                  <input className="input" type="number" min="1" placeholder="2" value={form.occupancy} onChange={e => setForm(f => ({ ...f, occupancy: e.target.value }))} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Move-in date</label>
                  <input className="input" type="date" value={form.moveInDate} onChange={e => setForm(f => ({ ...f, moveInDate: e.target.value }))} />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Amenities</label>
                <div className="flex flex-wrap gap-1">
                  {AMENITY_PRESETS.map(a => (
                    <button key={a} type="button" onClick={() => toggleAmenity(setForm)(a)}
                      className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${form.amenities.includes(a) ? 'bg-indigo-600 text-white border-transparent' : 'border-gray-200 text-gray-400 hover:border-indigo-300'}`}>
                      {a}
                    </button>
                  ))}
                </div>
              </div>

              <input className="input w-full" placeholder="Flatmate preferences (comma-sep: non-smoker, vegetarian, early riser)"
                value={form.preferencesInput} onChange={e => setForm(f => ({ ...f, preferencesInput: e.target.value }))} />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-end">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Contact (shown to interested users)</label>
                  <input className="input" placeholder="9876543210 or email" value={form.contact} onChange={e => setForm(f => ({ ...f, contact: e.target.value }))} />
                </div>
                <label className="flex items-center gap-2 text-sm text-gray-600 pb-2 cursor-pointer">
                  <input type="checkbox" className="rounded accent-indigo-600" checked={form.contactWhatsapp} onChange={e => setForm(f => ({ ...f, contactWhatsapp: e.target.checked }))} />
                  This number is on WhatsApp
                </label>
              </div>

              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowCreate(false)} className="btn-secondary flex-1">Cancel</button>
                <button type="submit" disabled={creating || photoBusy} className="btn-primary flex-1">
                  {creating ? 'Posting…' : photoBusy ? 'Processing photos…' : 'Post listing'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit modal */}
      {editTarget && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-gray-100 sticky top-0 bg-white z-10">
              <h2 className="font-bold text-gray-900">Edit listing</h2>
              <button onClick={() => setEditTarget(null)}><X size={18} className="text-gray-400" /></button>
            </div>
            <form onSubmit={handleUpdate} className="p-5 space-y-4">
              <input required className="input w-full" placeholder="Title" value={editForm.title} onChange={e => setEditForm(f => ({ ...f, title: e.target.value }))} />
              <textarea className="input w-full !h-20 resize-none text-sm" placeholder="Description" value={editForm.description} onChange={e => setEditForm(f => ({ ...f, description: e.target.value }))} />
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Budget max (₹/month)</label>
                  <input required className="input" type="number" min="1" value={editForm.budgetMax} onChange={e => setEditForm(f => ({ ...f, budgetMax: e.target.value }))} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <select className="input" value={editForm.status} onChange={e => setEditForm(f => ({ ...f, status: e.target.value }))}>
                    <option value="ACTIVE">Active</option>
                    <option value="CLOSED">Closed (found / filled)</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Amenities</label>
                <div className="flex flex-wrap gap-1">
                  {[...new Set([...AMENITY_PRESETS, ...editForm.amenities])].map(a => (
                    <button key={a} type="button" onClick={() => toggleAmenity(setEditForm)(a)}
                      className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${editForm.amenities.includes(a) ? 'bg-indigo-600 text-white border-transparent' : 'border-gray-200 text-gray-400 hover:border-indigo-300'}`}>
                      {a}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setEditTarget(null)} className="btn-secondary flex-1">Cancel</button>
                <button type="submit" disabled={updating} className="btn-primary flex-1">{updating ? 'Saving…' : 'Save changes'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
